/**
 * Reset Password Page Component (TASK-322)
 * Verifies the password-reset token on mount, then lets the user set a new password.
 */
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, KeyRound, Eye, EyeOff } from 'lucide-react'
import apiClient from '@/services/api/client'

const INVALID_TOKEN_MESSAGE = 'Недействительная или просроченная ссылка сброса'
const PASSWORDS_MISMATCH_MESSAGE = 'Пароли не совпадают.'
const RESET_SUCCESS_MESSAGE = 'Пароль успешно изменён.'
const FALLBACK_ERROR_MESSAGE = 'Не удалось сбросить пароль.'

/**
 * Mirrors backend `validate_password_strength` (see docs/tasks/TASK-322) so the
 * user gets instant feedback without a round trip. Order matters: length,
 * then letter, then digit.
 */
function validatePasswordClient(password: string): string | null {
  if (password.length < 8) return 'Пароль должен содержать минимум 8 символов.'
  if (!/[a-zA-Z]/.test(password)) return 'Пароль должен содержать хотя бы одну букву.'
  if (!/\d/.test(password)) return 'Пароль должен содержать хотя бы одну цифру.'
  return null
}

export function ResetPasswordPage() {
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>()
  const navigate = useNavigate()

  const [isVerifying, setIsVerifying] = useState(true)
  const [tokenValid, setTokenValid] = useState<boolean | null>(null)

  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [message, setMessage] = useState<string>(RESET_SUCCESS_MESSAGE)
  const [countdown, setCountdown] = useState(3)

  useEffect(() => {
    const verify = async () => {
      try {
        const response = await apiClient.get(
          `/api/v1/auth/password-reset/verify/${uidb64}/${token}/`
        )
        setTokenValid(response.data?.valid === true)
      } catch {
        // Any rejection (400, network error, etc.) is treated as an invalid token.
        setTokenValid(false)
      } finally {
        setIsVerifying(false)
      }
    }

    verify()
  }, [uidb64, token])

  useEffect(() => {
    if (!success) return
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          navigate('/login')
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [success, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== passwordConfirm) {
      setError(PASSWORDS_MISMATCH_MESSAGE)
      return
    }

    const clientError = validatePasswordClient(password)
    if (clientError) {
      setError(clientError)
      return
    }

    setIsSubmitting(true)

    try {
      const response = await apiClient.post('/api/v1/auth/password-reset/confirm/', {
        uid: uidb64,
        token,
        password,
        password_confirm: passwordConfirm,
      })
      setMessage(response.data?.message || RESET_SUCCESS_MESSAGE)
      setSuccess(true)
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } }
      setError(axiosError.response?.data?.error || FALLBACK_ERROR_MESSAGE)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-bold text-blue-600">
            SMETA AI
          </h1>
          <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
            Сброс пароля
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Платформа автоматизации строительных смет
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {isVerifying && (
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm text-gray-600">Проверяем ссылку...</p>
            </div>
          )}

          {!isVerifying && tokenValid === false && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>{INVALID_TOKEN_MESSAGE}</AlertDescription>
              </Alert>
              <p className="text-center text-sm text-gray-600">
                <Link
                  to="/forgot-password"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Запросить новую ссылку
                </Link>
              </p>
            </div>
          )}

          {!isVerifying && tokenValid === true && !success && (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                <div>
                  <Label htmlFor="password">Новый пароль</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Введите новый пароль"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="passwordConfirm">Подтверждение пароля</Label>
                  <div className="relative mt-1">
                    <Input
                      id="passwordConfirm"
                      name="passwordConfirm"
                      type={showPasswordConfirm ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      placeholder="Повторите новый пароль"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                      aria-label={showPasswordConfirm ? 'Скрыть пароль' : 'Показать пароль'}
                    >
                      {showPasswordConfirm ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Сброс пароля...
                  </>
                ) : (
                  <>
                    <KeyRound className="mr-2 h-4 w-4" />
                    Сбросить пароль
                  </>
                )}
              </Button>
            </form>
          )}

          {success && (
            <div className="space-y-4">
              <Alert>
                <AlertDescription>{message}</AlertDescription>
              </Alert>
              <p className="text-center text-sm text-gray-600">
                Переход на страницу входа через {countdown}...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
